import { useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink, AlertTriangle, BookOpen, Shield, Search, Zap, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import api from '../../api'
import { NODE_THEME } from './graphTheme'
import { NODE_TYPE_LABELS } from './graphTypes'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface NodeDetailPanelProps {
  nodeId: string | null
  onClose: () => void
  onNavigateToNode: (targetNodeId: string) => void
}

interface ConnectedNode {
  id: string
  label: string
  type: string
  relationship: string
}

interface NodeDetail {
  id: string
  type: string
  label: string
  metadata: Record<string, any>
  connected_nodes: ConnectedNode[]
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const TYPE_ICON: Record<string, typeof AlertTriangle> = {
  exception_type: AlertTriangle,
  resolution_pattern: Zap,
  xbrl_concept: BookOpen,
  reference_document: BookOpen,
  control_objective: Shield,
  failure_pattern: AlertTriangle,
  detection_rule: Search,
}

function TypeBadge({ type }: { type: string }) {
  const theme = NODE_THEME[type as keyof typeof NODE_THEME]
  const color = theme?.color ?? '#94A3B8'
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide"
      style={{ color, border: `1px solid ${color}40`, background: `${color}12` }}
    >
      {NODE_TYPE_LABELS[type] ?? type}
    </span>
  )
}

function FrameworkBadge({ framework }: { framework?: string }) {
  if (!framework) return null
  const color = framework === 'IFRS' ? '#FB923C' : '#60A5FA'
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold tracking-wide"
      style={{ color, border: `1px solid ${color}40`, background: `${color}12` }}
    >
      {framework}
    </span>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-widest text-[#64748B]">
      {children}
    </h3>
  )
}

function PropRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="shrink-0 text-xs text-[#64748B]">{label}</span>
      <span className="text-right text-xs font-medium text-[#CBD5E1] break-words min-w-0">
        {typeof value === 'number' ? value.toLocaleString() : String(value)}
      </span>
    </div>
  )
}

function ConnectedNodeLink({
  node,
  onClick,
}: {
  node: ConnectedNode
  onClick: () => void
}) {
  const theme = NODE_THEME[node.type as keyof typeof NODE_THEME]
  const color = theme?.color ?? '#94A3B8'
  return (
    <button
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left',
        'hover:bg-white/[0.04] transition-colors',
      )}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
      <span className="min-w-0 flex-1 truncate text-xs text-[#CBD5E1] group-hover:text-[#F0F2F5]">
        {node.label}
      </span>
      <span className="shrink-0 text-[10px] text-[#475569]">{node.relationship}</span>
      <ChevronRight className="h-3 w-3 shrink-0 text-[#475569] opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

/* ------------------------------------------------------------------ */
/*  Per-type detail sections                                           */
/* ------------------------------------------------------------------ */

function ExceptionTypeDetail({ m }: { m: Record<string, any> }) {
  return (
    <>
      <PropRow label="Department" value={m.department} />
      <PropRow label="Default Severity" value={m.default_severity} />
      <PropRow label="Exceptions in DB" value={m.exception_count} />
      <PropRow label="Framework" value={m.framework} />
    </>
  )
}

function ResolutionPatternDetail({ m }: { m: Record<string, any> }) {
  return (
    <>
      <PropRow label="Framework" value={m.framework} />
      <PropRow label="Type" value={m.resolution_type ?? 'JOURNAL_ENTRY'} />
      <PropRow label="Standard" value={m.standard_reference} />
      <PropRow label="Source" value={m.source} />
      <PropRow label="Usage" value={m.usage_count != null ? `${m.usage_count} times` : undefined} />
      {m.debit_account && (
        <>
          <SectionHeading>JE Template</SectionHeading>
          <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2 font-mono text-[11px]">
            <div className="text-[#60A5FA]">DR  {m.debit_account}</div>
            <div className="text-[#FB923C]">CR  {m.credit_account}</div>
          </div>
        </>
      )}
      {m.citation && (
        <>
          <SectionHeading>Citation</SectionHeading>
          <p className="text-xs italic leading-relaxed text-[#94A3B8]">"{m.citation}"</p>
        </>
      )}
    </>
  )
}

function XBRLConceptDetail({ m }: { m: Record<string, any> }) {
  return (
    <>
      <PropRow label="Element" value={m.element_name} />
      <PropRow label="Normal Balance" value={m.normal_balance} />
      <PropRow label="Period Type" value={m.period_type} />
      <PropRow label="Statement" value={m.statement} />
      <PropRow label="Account Type" value={m.account_type} />
      <PropRow label="ASC Reference" value={m.asc_reference} />
      <PropRow label="Parent" value={m.parent_element} />
      {m.definition && (
        <>
          <SectionHeading>Definition</SectionHeading>
          <p className="text-xs leading-relaxed text-[#94A3B8]">{m.definition}</p>
        </>
      )}
      {m.ifrs_equivalent && (
        <>
          <SectionHeading>IFRS Equivalent</SectionHeading>
          <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-xs text-[#FB923C]">
            {m.ifrs_equivalent}
            {m.mapping_type && <span className="ml-2 text-[#475569]">({m.mapping_type})</span>}
          </div>
        </>
      )}
    </>
  )
}

function ReferenceDocDetail({ m }: { m: Record<string, any> }) {
  return (
    <>
      <PropRow label="Code" value={m.code} />
      <PropRow label="Title" value={m.title} />
      <PropRow label="Issuing Body" value={m.issuing_body} />
      <PropRow label="Framework" value={m.framework} />
    </>
  )
}

function ControlObjectiveDetail({ m }: { m: Record<string, any> }) {
  return (
    <>
      <PropRow label="Business Cycle" value={m.business_cycle} />
      <PropRow label="Framework" value={m.framework} />
      <PropRow label="Control Type" value={m.control_type} />
      {m.description && (
        <>
          <SectionHeading>Description</SectionHeading>
          <p className="text-xs leading-relaxed text-[#94A3B8]">{m.description}</p>
        </>
      )}
    </>
  )
}

function FailurePatternDetail({ m }: { m: Record<string, any> }) {
  return (
    <>
      <PropRow label="Severity" value={m.severity} />
      <PropRow label="Business Cycle" value={m.business_cycle} />
      {m.description && (
        <>
          <SectionHeading>Description</SectionHeading>
          <p className="text-xs leading-relaxed text-[#94A3B8]">{m.description}</p>
        </>
      )}
    </>
  )
}

function DetectionRuleDetail({ m }: { m: Record<string, any> }) {
  return (
    <>
      <PropRow label="Source Table" value={m.source_table} />
      <PropRow label="Condition Logic" value={m.condition_logic} />
      <PropRow label="Action" value={m.action_on_match} />
      {m.conditions && (
        <>
          <SectionHeading>Conditions</SectionHeading>
          <div className="space-y-1">
            {(m.conditions as any[]).map((c: any, i: number) => (
              <div
                key={i}
                className="rounded border border-white/[0.06] bg-white/[0.02] px-2 py-1 font-mono text-[10px] text-[#FACC15]"
              >
                {c.field} {c.operator} {c.value !== undefined ? JSON.stringify(c.value) : ''}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

const DETAIL_RENDERERS: Record<string, React.FC<{ m: Record<string, any> }>> = {
  exception_type: ExceptionTypeDetail,
  resolution_pattern: ResolutionPatternDetail,
  xbrl_concept: XBRLConceptDetail,
  reference_document: ReferenceDocDetail,
  control_objective: ControlObjectiveDetail,
  failure_pattern: FailurePatternDetail,
  detection_rule: DetectionRuleDetail,
}

/* ------------------------------------------------------------------ */
/*  Panel component                                                    */
/* ------------------------------------------------------------------ */

export function NodeDetailPanel({ nodeId, onClose, onNavigateToNode }: NodeDetailPanelProps) {
  const open = nodeId !== null

  const { data: nodeDetail, isLoading, error, refetch } = useQuery<NodeDetail>({
    queryKey: ['graph-node', nodeId],
    queryFn: () =>
      api.get(`/knowledge-engine/graph/node/${encodeURIComponent(nodeId!)}`)
        .then(r => r.data),
    enabled: !!nodeId,
    staleTime: 5 * 60 * 1000,
  })

  /* Escape to close */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (open) document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleKeyDown])

  /* Group connected nodes by type */
  const connectedByType = (nodeDetail?.connected_nodes ?? []).reduce<Record<string, ConnectedNode[]>>(
    (acc, n) => {
      ;(acc[n.type] ??= []).push(n)
      return acc
    },
    {},
  )

  const DetailRenderer = nodeDetail ? DETAIL_RENDERERS[nodeDetail.type] : undefined
  const Icon = nodeDetail ? TYPE_ICON[nodeDetail.type] ?? ExternalLink : ExternalLink

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={cn(
              'fixed right-0 top-0 z-50 h-full w-[450px] max-w-[90vw]',
              'flex flex-col',
              'border-l border-[#1E293B] bg-[#0F172A]',
              'shadow-2xl',
            )}
          >
            {/* ── Header ── */}
            <div className="flex items-start justify-between border-b border-[#1E293B] px-5 py-4">
              <div className="min-w-0 flex-1">
                {nodeDetail && (
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <TypeBadge type={nodeDetail.type} />
                    <FrameworkBadge framework={nodeDetail.metadata?.framework} />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-[#64748B]" />
                  <h2 className="text-base font-semibold leading-tight text-[#F0F2F5] truncate">
                    {nodeDetail?.label ?? 'Loading...'}
                  </h2>
                </div>
                {nodeDetail && (
                  <p className="mt-1 font-mono text-[10px] text-[#475569] truncate">
                    {nodeDetail.id}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="rounded-md p-1.5 text-[#64748B] hover:bg-white/[0.06] hover:text-[#F0F2F5] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* ── Content ── */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {isLoading && (
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-4 animate-pulse rounded bg-white/[0.04]" />
                  ))}
                </div>
              )}

              {nodeDetail && (
                <>
                  {/* Properties */}
                  <SectionHeading>Properties</SectionHeading>
                  <div className="divide-y divide-white/[0.04]">
                    {DetailRenderer && <DetailRenderer m={nodeDetail.metadata} />}
                  </div>

                  {/* Connected nodes */}
                  {Object.keys(connectedByType).length > 0 && (
                    <>
                      <SectionHeading>Connected Nodes</SectionHeading>
                      <div className="space-y-3">
                        {Object.entries(connectedByType).map(([type, nodes]) => (
                          <div key={type}>
                            <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-[#475569]">
                              {NODE_TYPE_LABELS[type] ?? type} ({nodes.length})
                            </p>
                            <div className="space-y-0.5">
                              {nodes.map(n => (
                                <ConnectedNodeLink
                                  key={n.id}
                                  node={n}
                                  onClick={() => onNavigateToNode(n.id)}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {!isLoading && error && (
                <div className="text-sm text-red-400 p-4">
                  <p className="font-medium mb-1">Failed to load node details</p>
                  <p className="text-xs text-[#64748B]">{(error as Error).message}</p>
                  <button
                    onClick={() => refetch()}
                    className="mt-2 text-xs text-[#2563EB] hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!isLoading && !error && !nodeDetail && (
                <p className="text-sm text-[#64748B] p-4">Node not found.</p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
